import { CoinIcon } from '../components/ui/CoinIcon';
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { CheckCircle, Clock, Gift, XCircle, Loader2, AlertCircle, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HOOKS = [
  { icon: '🎁', text: 'Gratis 10 Coin Guru saat daftar — langsung bisa bikin dokumen pertama!' },
  { icon: '⏰', text: 'Tidak ada tagihan bulanan. Bayar hanya saat butuh.' },
  { icon: '♾️',  text: 'Saldo tidak pernah expired. Topup sekali, pakai kapan saja.' },
  { icon: '🔒', text: 'Pembayaran aman via Midtrans — bank transfer, QRIS, kartu kredit, e-wallet.' },
  { icon: '🏫', text: 'Cocok untuk guru produktif yang butuh banyak dokumen setiap semester.' },
];

const fmt = n => new Intl.NumberFormat('id-ID').format(n);

// Load Midtrans Snap JS secara dinamis
const loadSnapScript = (clientKey, isProduction) => {
  return new Promise((resolve, reject) => {
    if (window.snap) { resolve(); return; }
    const existing = document.getElementById('midtrans-snap');
    if (existing) { existing.addEventListener('load', resolve); return; }

    const script = document.createElement('script');
    script.id = 'midtrans-snap';
    script.src = isProduction
      ? 'https://app.midtrans.com/snap/snap.js'
      : 'https://app.sandbox.midtrans.com/snap/snap.js';
    script.setAttribute('data-client-key', clientKey);
    script.onload  = resolve;
    script.onerror = () => reject(new Error('Gagal memuat Midtrans Snap JS'));
    document.head.appendChild(script);
  });
};

// ── Status states ──
const STATUS = {
  IDLE:     'idle',
  LOADING:  'loading',    // sedang request snap token
  SNAP_OPEN:'snap_open',  // popup Snap sedang terbuka
  POLLING:  'polling',    // menunggu konfirmasi webhook
  SUCCESS:  'success',    // bayar berhasil
  PENDING:  'pending',    // menunggu transfer (bank transfer/VA)
  FAILED:   'failed',     // gagal / batal
  ERROR:    'error',      // error teknis
};

export const Topup = () => {
  const [balance, setBalance]         = useState(null);
  const [packages, setPackages]       = useState([]);
  const [selected, setSelected]       = useState(null);
  const [status, setStatus]           = useState(STATUS.IDLE);
  const [errorMsg, setErrorMsg]       = useState('');
  const [successData, setSuccessData] = useState(null);
  const [pendingOrder, setPendingOrder] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [txPage, setTxPage]           = useState(1);
  const [pollCount, setPollCount]     = useState(0);
  const TX_PER_PAGE = 5;
  const paymentResultReceived         = useRef(false);

  const refreshBalance = useCallback(() => {
    axios.get(`${API}/coins/balance`, { withCredentials: true })
      .then(r => {
        setBalance(r.data.coin_balance);
        window.dispatchEvent(new Event('coinBalanceUpdated'));
      }).catch(() => {});
  }, []);

  useEffect(() => {
    refreshBalance();
    axios.get(`${API}/coins/packages`,     { withCredentials: true }).then(r => setPackages(r.data)).catch(() => {});
    axios.get(`${API}/coins/transactions`, { withCredentials: true }).then(r => setTransactions(r.data)).catch(() => {});
  }, [refreshBalance]);

  // ── Polling saat status PENDING ──
  useEffect(() => {
    if (status !== STATUS.POLLING && status !== STATUS.PENDING) return;
    if (!pendingOrder) return;

    const interval = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${pendingOrder.order_id}`, { withCredentials: true });
        setPollCount(c => c + 1);

        if (data.status === 'paid') {
          clearInterval(interval);
          setSuccessData(data);
          setBalance(data.coin_balance);
          setStatus(STATUS.SUCCESS);
          // Beritahu navbar/sidebar agar update saldo
          window.dispatchEvent(new Event('coinBalanceUpdated'));
          // Refresh riwayat
          axios.get(`${API}/coins/transactions`, { withCredentials: true }).then(r => setTransactions(r.data)).catch(() => {});
        } else if (data.status === 'failed') {
          clearInterval(interval);
          setStatus(STATUS.FAILED);
          setErrorMsg('Pembayaran dibatalkan atau gagal.');
        }
      } catch { /* ignore */ }
    }, 3000);

    // Stop polling setelah 10 menit
    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (status === STATUS.POLLING) {
        setStatus(STATUS.PENDING);
      }
    }, 10 * 60 * 1000);

    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [status, pendingOrder]);

  const handleBuy = async () => {
    if (!selected) return;
    setStatus(STATUS.LOADING);
    setErrorMsg('');

    try {
      // 1. Buat transaksi di backend → dapat snap_token
      const { data } = await axios.post(
        `${API}/payments/create-transaction`,
        { package_id: selected.id },
        { withCredentials: true }
      );

      const { snap_token, order_id, client_key, is_production } = data;

      // 2. Load Snap JS
      await loadSnapScript(client_key, is_production);

      setPendingOrder({ order_id, total_coins: selected.coins + selected.bonus });
      setStatus(STATUS.SNAP_OPEN);
      paymentResultReceived.current = false;

      // 3. Buka popup Snap
      window.snap.pay(snap_token, {
        onSuccess: () => {
          paymentResultReceived.current = true;
          setStatus(STATUS.POLLING);
        },
        onPending: () => {
          paymentResultReceived.current = true;
          setStatus(STATUS.PENDING);
        },
        onError: (result) => {
          paymentResultReceived.current = true;
          setStatus(STATUS.FAILED);
          setErrorMsg(result?.status_message || 'Pembayaran gagal. Silakan coba lagi.');
        },
        onClose: () => {
          // Hanya reset ke IDLE kalau user tutup popup TANPA bayar
          if (!paymentResultReceived.current) {
            setStatus(STATUS.IDLE);
          }
        },
      });

    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Terjadi kesalahan.';
      setErrorMsg(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setStatus(STATUS.ERROR);
    }
  };

  const reset = () => {
    setStatus(STATUS.IDLE);
    setErrorMsg('');
    setSuccessData(null);
    setPendingOrder(null);
    setPollCount(0);
    setSelected(null);
    refreshBalance();
    axios.get(`${API}/coins/transactions`, { withCredentials: true }).then(r => setTransactions(r.data)).catch(() => {});
  };

  const formatDate = d => new Date(d).toLocaleDateString('id-ID', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  // ── Status screens ──────────────────────────────────────────────────────────

  if (status === STATUS.SUCCESS && successData) {
    return (
      <DashboardLayout>
        <div className="max-w-md mx-auto mt-16 text-center" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
            <CheckCircle size={40} className="text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Pembayaran Berhasil!
          </h2>
          <p className="text-slate-500 text-sm mb-1">
            <strong className="text-green-600">+{successData.total_coins} Coin Guru</strong> telah ditambahkan ke saldo Anda.
          </p>
          <p className="text-slate-400 text-xs mb-8">
            Saldo sekarang: <strong className="text-slate-700">{successData.coin_balance} koin</strong>
          </p>
          <div className="flex flex-col gap-3">
            <button onClick={() => window.location.href = '/documents/generate'}
              className="w-full py-3 rounded-2xl text-white font-bold text-sm shadow-md hover:-translate-y-0.5 transition-all"
              style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>
              🚀 Buat Dokumen Sekarang
            </button>
            <button onClick={reset}
              className="w-full py-2.5 rounded-2xl border-2 border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 transition-colors">
              Topup Lagi
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (status === STATUS.POLLING) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[70vh]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
          <div className="text-center max-w-sm">
            {/* Spinner */}
            <div className="relative w-20 h-20 mx-auto mb-6">
              <svg className="animate-spin w-20 h-20 text-blue-100" fill="none" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" />
              </svg>
              <svg className="animate-spin w-20 h-20 absolute inset-0" style={{ animationDuration: '0.8s' }} fill="none" viewBox="0 0 24 24">
                <path className="text-blue-600" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <CoinIcon size={22} />
              </div>
            </div>

            <h2 className="text-xl font-bold text-slate-800 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Memverifikasi Pembayaran...
            </h2>
            <p className="text-slate-500 text-sm mb-1">
              Sedang mengkonfirmasi pembayaran Anda.
            </p>
            <p className="text-slate-400 text-xs mb-6">
              Mohon tunggu, jangan tutup atau refresh halaman ini.
            </p>

            {/* Animated dots */}
            <div className="flex items-center justify-center gap-1.5">
              {[0, 1, 2].map(i => (
                <div key={i} className="w-2 h-2 rounded-full bg-blue-400"
                  style={{ animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite` }} />
              ))}
            </div>

            <style>{`
              @keyframes bounce {
                0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
                40% { transform: translateY(-6px); opacity: 1; }
              }
            `}</style>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (status === STATUS.PENDING) {
    return (
      <DashboardLayout>
        <div className="max-w-md mx-auto mt-16 text-center" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
          <div className="w-20 h-20 rounded-full bg-yellow-50 flex items-center justify-center mx-auto mb-5">
            <Clock size={36} className="text-yellow-500" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Menunggu Pembayaran
          </h2>
          <p className="text-slate-500 text-sm mb-2">
            Selesaikan pembayaran sesuai instruksi yang dikirim Midtrans.
            Saldo akan otomatis bertambah setelah transfer dikonfirmasi.
          </p>
          {pendingOrder && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3 mb-6 text-left">
              <p className="text-xs text-yellow-700 font-medium">Order ID</p>
              <p className="text-sm font-mono text-slate-700">{pendingOrder.order_id}</p>
              {pollCount > 0 && (
                <p className="text-xs text-yellow-600 mt-1 flex items-center gap-1">
                  <RefreshCw size={10} className="animate-spin" /> Memeriksa status... ({pollCount}x)
                </p>
              )}
            </div>
          )}
          <div className="flex flex-col gap-3">
            <button onClick={reset}
              className="w-full py-2.5 rounded-2xl border-2 border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 transition-colors">
              Kembali ke Halaman Topup
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (status === STATUS.FAILED || status === STATUS.ERROR) {
    return (
      <DashboardLayout>
        <div className="max-w-md mx-auto mt-16 text-center" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
          <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-5">
            <XCircle size={36} className="text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Pembayaran Gagal
          </h2>
          {errorMsg && (
            <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 mb-5">
              <p className="text-red-600 text-sm">{errorMsg}</p>
            </div>
          )}
          <p className="text-slate-400 text-xs mb-6">Tidak ada dana yang dipotong. Coba lagi kapan saja.</p>
          <button onClick={reset}
            className="w-full py-3 rounded-2xl text-white font-bold text-sm shadow-md"
            style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>
            Coba Lagi
          </button>
        </div>
      </DashboardLayout>
    );
  }

  // ── Main topup page ─────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-blue-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <span className="inline-flex items-center gap-2"><CoinIcon size={24} /> Coin Guru</span>
          </h1>
          <p className="text-slate-500 text-sm">Isi saldo untuk membuat dokumen pembelajaran dengan AI</p>
        </div>

        {/* Saldo card */}
        <div className="rounded-2xl p-6 mb-8 text-white relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>
          <div className="absolute right-0 top-0 w-48 h-48 rounded-full opacity-10"
            style={{ background: 'white', transform: 'translate(30%, -30%)' }} />
          <div className="absolute right-12 bottom-0 w-32 h-32 rounded-full opacity-5"
            style={{ background: 'white', transform: 'translateY(40%)' }} />
          <p className="text-blue-200 text-sm font-medium mb-1">Saldo Coin Guru Anda</p>
          <div className="flex items-end gap-3">
            <span className="text-5xl font-bold">{balance ?? '...'}</span>
            <span className="text-blue-200 text-lg pb-1">koin</span>
          </div>
          <p className="text-blue-200 text-xs mt-2">
            Setara ≈ Rp {balance !== null ? fmt(balance * 2000) : '...'}
            &nbsp;·&nbsp;Saldo tidak pernah expired
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">

          {/* Paket Topup */}
          <div className="lg:col-span-2">
            <h2 className="font-bold text-slate-800 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Pilih Paket Topup
            </h2>

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              {packages.map(pkg => {
                const total = pkg.coins + pkg.bonus;
                const isSelected = selected?.id === pkg.id;
                return (
                  <button
                    key={pkg.id}
                    onClick={() => setSelected(pkg)}
                    className={`relative text-left p-5 rounded-2xl border-2 transition-all hover:shadow-md ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 shadow-md'
                        : 'border-slate-200 bg-white hover:border-blue-200'
                    }`}
                  >
                    {pkg.id === 'hemat' && (
                      <div className="absolute -top-2.5 left-4 px-2.5 py-0.5 rounded-full text-xs font-bold text-white"
                        style={{ background: 'linear-gradient(135deg, #F59E0B, #EF4444)' }}>
                        ⭐ Terpopuler
                      </div>
                    )}
                    {pkg.bonus > 0 && (
                      <div className="absolute -top-2.5 right-4 px-2.5 py-0.5 bg-green-500 text-white rounded-full text-xs font-bold">
                        +{pkg.bonus} bonus
                      </div>
                    )}

                    <div className="flex items-center justify-between mb-3">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                        isSelected ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
                      }`}>{pkg.label}</span>
                      {isSelected && <CheckCircle size={16} className="text-blue-500" />}
                    </div>

                    <div className="flex items-baseline gap-1 mb-0.5">
                      <span className="text-3xl font-bold text-slate-800">{total}</span>
                      <span className="text-slate-500 text-sm">koin</span>
                    </div>
                    {pkg.bonus > 0 && (
                      <p className="text-xs text-green-600 font-medium mb-2">
                        {pkg.coins} koin + {pkg.bonus} bonus gratis
                      </p>
                    )}

                    <div className="border-t border-slate-100 mt-3 pt-3 flex items-center justify-between">
                      <span className="text-slate-400 text-xs">
                        Rp {fmt(Math.round(pkg.price / total))}/koin
                      </span>
                      <span className="font-bold text-blue-900 text-base">
                        Rp {fmt(pkg.price)}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Tombol bayar */}
            <button
              onClick={handleBuy}
              disabled={!selected || status === STATUS.LOADING || status === STATUS.SNAP_OPEN}
              className="w-full py-3.5 rounded-2xl text-white font-bold text-base shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              style={{ background: selected ? 'linear-gradient(135deg, #F59E0B, #EF4444)' : '#94A3B8' }}
            >
              {status === STATUS.LOADING ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 size={16} className="animate-spin" />
                  Membuka halaman pembayaran...
                </span>
              ) : status === STATUS.SNAP_OPEN ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 size={16} className="animate-spin" />
                  Pembayaran sedang berlangsung...
                </span>
              ) : selected ? (
                `Bayar ${selected.coins + selected.bonus} Coin Guru — Rp ${fmt(selected.price)}`
              ) : (
                'Pilih paket di atas'
              )}
            </button>

            {/* Payment badges */}
            <div className="flex items-center justify-center gap-3 mt-4">
              <span className="text-xs text-slate-400">Metode pembayaran:</span>
              {['🏦 Transfer Bank', '📱 QRIS', '💳 Kartu Kredit', '💚 GoPay'].map(m => (
                <span key={m} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md font-medium">{m}</span>
              ))}
            </div>
            <p className="text-center text-xs text-slate-400 mt-2">
              🔒 Pembayaran aman via Midtrans · Saldo masuk otomatis setelah konfirmasi
            </p>
          </div>

          {/* Sidebar kanan */}
          <div className="flex flex-col gap-5">

            {/* Hook marketing */}
            <div className="bg-white border border-blue-100 rounded-2xl p-5 shadow-sm">
              <h3 className="font-bold text-slate-700 text-sm mb-4 flex items-center gap-2">
                <Gift size={15} className="text-yellow-500" />
                Kenapa Coin Guru?
              </h3>
              <ul className="flex flex-col gap-3">
                {HOOKS.map((h, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="text-base leading-none flex-shrink-0 mt-0.5">{h.icon}</span>
                    <span className="text-xs text-slate-600 leading-relaxed">{h.text}</span>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>

        {/* Riwayat transaksi — full width di bawah paket */}
        {transactions.length > 0 && (() => {
          const totalPages = Math.ceil(transactions.length / TX_PER_PAGE);
          const paginated  = transactions.slice((txPage - 1) * TX_PER_PAGE, txPage * TX_PER_PAGE);
          return (
            <div className="mt-6 bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <Clock size={15} className="text-slate-400" />
                  <h3 className="font-bold text-slate-700 text-sm">Riwayat Transaksi</h3>
                </div>
                <span className="text-xs text-slate-400">{transactions.length} transaksi</span>
              </div>

              {/* Rows */}
              <div className="divide-y divide-slate-50">
                {paginated.map(tx => (
                  <div key={tx._id} className="flex items-center justify-between px-6 py-3.5 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                        tx.coins > 0 ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        {tx.coins > 0
                          ? <CoinIcon size={18} />
                          : <span className="text-red-500 text-sm font-bold">−</span>
                        }
                      </div>
                      <div>
                        <p className="text-sm text-slate-700 font-medium leading-tight">{tx.description}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{formatDate(tx.created_at)}</p>
                      </div>
                    </div>
                    <span className={`text-sm font-bold flex-shrink-0 ${tx.coins > 0 ? 'text-green-600' : 'text-red-500'}`}>
                      {tx.coins > 0 ? '+' : ''}{tx.coins} koin
                    </span>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-3.5 border-t border-slate-100 bg-slate-50">
                  <span className="text-xs text-slate-500">
                    Halaman {txPage} dari {totalPages}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setTxPage(p => Math.max(1, p - 1))}
                      disabled={txPage === 1}
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      ‹
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                      <button
                        key={p}
                        onClick={() => setTxPage(p)}
                        className={`w-8 h-8 rounded-lg text-xs font-semibold transition-colors ${
                          p === txPage
                            ? 'text-white shadow-sm'
                            : 'text-slate-500 hover:bg-slate-200'
                        }`}
                        style={p === txPage ? { background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' } : {}}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      onClick={() => setTxPage(p => Math.min(totalPages, p + 1))}
                      disabled={txPage === totalPages}
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      ›
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

      </div>
    </DashboardLayout>
  );
};
